// @oasis-permission-inherits: /videos (mirage-videos:view)
import { Link, createFileRoute } from "@tanstack/react-router";
import {
  Check,
  Loader2,
  Pause,
  Play,
  RefreshCw,
  RotateCcw,
  Trophy,
  Volume2,
  X,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import { Button } from "~/components/ui/button";
import { cn } from "~/lib/utils";
import { submitMirageVideoEvalVoteMutation } from "~/modules/mirage-video-evals/mutations";
import {
  getMirageVideoEvalNextPairQuery,
  getMirageVideoEvalTestQuery,
} from "~/modules/mirage-video-evals/queries";
import type {
  MirageVideoEvalPair,
  MirageVideoEvalVoteWinner,
} from "~/modules/mirage-video-evals/schemas";

export const Route = createFileRoute("/_dash/videos/evals/$testId/rank")({
  component: RouteComponent,
});

function RouteComponent() {
  const { testId } = Route.useParams();
  const [isVoting, setIsVoting] = useState(false);
  const { data: test } = getMirageVideoEvalTestQuery.useQuery({
    pathParams: { testId },
  });
  const { data, isLoading, error, refetch, isFetching } = getMirageVideoEvalNextPairQuery.useQuery({
    pathParams: { testId },
  });
  const pair = data?.pair ?? null;

  const submitVote = useCallback(
    async (winner: MirageVideoEvalVoteWinner) => {
      if (!pair) {
        return;
      }
      setIsVoting(true);
      try {
        await submitMirageVideoEvalVoteMutation.trigger({
          pathParams: { testId },
          params: {
            leftVideoId: pair.left.videoId,
            rightVideoId: pair.right.videoId,
            winner,
          },
        });
        await refetch();
      } catch (error) {
        toast.error("Failed to submit vote", {
          description: error instanceof Error ? error.message : "Unknown error",
        });
      } finally {
        setIsVoting(false);
      }
    },
    [pair, refetch, testId]
  );

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.repeat || isVoting || !pair) {
        return;
      }
      const target = event.target;
      if (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLSelectElement
      ) {
        return;
      }
      if (event.key.toLowerCase() === "a") {
        void submitVote("left");
      } else if (event.key.toLowerCase() === "b") {
        void submitVote("right");
      } else if (event.key.toLowerCase() === "t") {
        void submitVote("tie");
      } else if (event.key.toLowerCase() === "x") {
        void submitVote("both_bad");
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isVoting, pair, submitVote]);

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="mb-2">
            <Button asChild variant="link" size="sm" className="h-auto p-0">
              <Link to="/videos/evals/$testId" params={{ testId }}>
                Back to test
              </Link>
            </Button>
          </div>
          <h1 className="text-xl font-bold">{test?.name ?? "Mirage Eval Ranking"}</h1>
          <div className="mt-1 flex items-center gap-2 text-sm text-muted-foreground">
            <span>{testId}</span>
            {pair && (
              <>
                <span>|</span>
                <span>{pair.clipId}</span>
                <span>|</span>
                <span>{pair.totalVotesForPair} votes on this pair</span>
              </>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
            <RefreshCw className={cn("size-4", isFetching && "animate-spin")} />
            Refresh
          </Button>
          <Button asChild variant="outline" size="sm">
            <Link to="/videos/evals/$testId/results" params={{ testId }}>
              <Trophy className="size-4" />
              Results
            </Link>
          </Button>
        </div>
      </div>

      {isLoading && (
        <div className="rounded-md border p-5 text-sm text-muted-foreground">Loading pair...</div>
      )}

      {error && (
        <div className="rounded-md border border-destructive bg-destructive/10 p-4 text-sm text-destructive">
          <p className="font-medium">Failed to load next pair</p>
          <pre className="mt-2 whitespace-pre-wrap text-xs">{error.message}</pre>
        </div>
      )}

      {!isLoading && !error && !pair && (
        <div className="rounded-md border p-5 text-sm text-muted-foreground">
          No comparable pair is available.
        </div>
      )}

      {pair && (
        <>
          <SyncedBlindComparison pair={pair} />

          <div className="sticky bottom-4 rounded-md border bg-background/95 p-3 shadow-sm backdrop-blur">
            <div className="grid gap-2 sm:grid-cols-4">
              <Button onClick={() => submitVote("left")} disabled={isVoting}>
                {isVoting ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <Check className="size-4" />
                )}
                A wins
              </Button>
              <Button onClick={() => submitVote("right")} disabled={isVoting}>
                {isVoting ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <Check className="size-4" />
                )}
                B wins
              </Button>
              <Button variant="secondary" onClick={() => submitVote("tie")} disabled={isVoting}>
                Tie
              </Button>
              <Button variant="outline" onClick={() => submitVote("both_bad")} disabled={isVoting}>
                <X className="size-4" />
                Both bad
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function SyncedBlindComparison({ pair }: { pair: MirageVideoEvalPair }) {
  const leftVideoRef = useRef<HTMLVideoElement | null>(null);
  const rightVideoRef = useRef<HTMLVideoElement | null>(null);
  const [audioSide, setAudioSide] = useState<"left" | "right">("left");
  const [isPlaying, setIsPlaying] = useState(false);

  const setLeftVideoRef = useCallback((node: HTMLVideoElement | null) => {
    leftVideoRef.current = node;
    if (node) {
      node.muted = true;
    }
  }, []);

  const setRightVideoRef = useCallback((node: HTMLVideoElement | null) => {
    rightVideoRef.current = node;
    if (node) {
      node.muted = true;
    }
  }, []);

  const videos = useCallback(() => {
    return [leftVideoRef.current, rightVideoRef.current].filter(
      (video): video is HTMLVideoElement => video !== null
    );
  }, []);

  const applyAudioSide = useCallback(() => {
    if (leftVideoRef.current) {
      leftVideoRef.current.muted = audioSide !== "left";
    }
    if (rightVideoRef.current) {
      rightVideoRef.current.muted = audioSide !== "right";
    }
  }, [audioSide]);

  const syncTo = useCallback(
    (time: number, force = false) => {
      for (const video of videos()) {
        if (force || Math.abs(video.currentTime - time) > 0.06) {
          video.currentTime = time;
        }
      }
    },
    [videos]
  );

  const play = useCallback(async () => {
    applyAudioSide();
    const playableVideos = videos();
    const master = audioSide === "left" ? leftVideoRef.current : rightVideoRef.current;
    if (master) {
      syncTo(master.ended ? 0 : master.currentTime, true);
    }
    const results = await Promise.allSettled(playableVideos.map((video) => video.play()));
    if (results.some((result) => result.status === "rejected")) {
      for (const video of playableVideos) {
        video.pause();
      }
      setIsPlaying(false);
      toast.error("Playback was blocked", {
        description: "Click play again after the videos finish loading.",
      });
      return;
    }
    setIsPlaying(true);
  }, [applyAudioSide, audioSide, syncTo, videos]);

  const pause = useCallback(() => {
    for (const video of videos()) {
      video.pause();
    }
    setIsPlaying(false);
  }, [videos]);

  const togglePlayback = useCallback(() => {
    if (isPlaying) {
      pause();
    } else {
      void play();
    }
  }, [isPlaying, pause, play]);

  const restart = useCallback(() => {
    syncTo(0, true);
    if (isPlaying) {
      void play();
    }
  }, [isPlaying, play, syncTo]);

  useEffect(() => {
    setIsPlaying(false);
    setAudioSide("left");
    for (const video of videos()) {
      video.pause();
      video.currentTime = 0;
    }
  }, [pair.left.videoId, pair.right.videoId, videos]);

  useEffect(() => {
    applyAudioSide();
  }, [applyAudioSide]);

  useEffect(() => {
    if (!isPlaying) {
      return;
    }

    let frame = 0;
    const tick = () => {
      const master = audioSide === "left" ? leftVideoRef.current : rightVideoRef.current;
      if (master && !master.paused) {
        syncTo(master.currentTime);
      }
      frame = window.requestAnimationFrame(tick);
    };
    frame = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(frame);
  }, [audioSide, isPlaying, syncTo]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.repeat) {
        return;
      }
      const target = event.target;
      if (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLSelectElement
      ) {
        return;
      }
      if (event.key === " ") {
        event.preventDefault();
        togglePlayback();
      } else if (event.key.toLowerCase() === "r") {
        restart();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [restart, togglePlayback]);

  return (
    <div className="space-y-3">
      <div className="grid gap-4 lg:grid-cols-2">
        <BlindVideo
          label="A"
          refCallback={setLeftVideoRef}
          videoId={pair.left.videoId}
          url={pair.left.url}
        />
        <BlindVideo
          label="B"
          refCallback={setRightVideoRef}
          videoId={pair.right.videoId}
          url={pair.right.url}
        />
      </div>
      <div className="flex flex-wrap items-center gap-2 rounded-md border bg-background p-2">
        <Button type="button" size="sm" onClick={togglePlayback}>
          {isPlaying ? <Pause className="size-4" /> : <Play className="size-4" />}
          {isPlaying ? "Pause" : "Play"}
        </Button>
        <Button type="button" size="sm" variant="outline" onClick={restart}>
          <RotateCcw className="size-4" />
          Restart
        </Button>
        <div className="flex overflow-hidden rounded-md border">
          <Button
            type="button"
            size="sm"
            variant={audioSide === "left" ? "secondary" : "ghost"}
            className="rounded-none border-r"
            onClick={() => setAudioSide("left")}
          >
            <Volume2 className="size-4" />A audio
          </Button>
          <Button
            type="button"
            size="sm"
            variant={audioSide === "right" ? "secondary" : "ghost"}
            className="rounded-none"
            onClick={() => setAudioSide("right")}
          >
            <Volume2 className="size-4" />B audio
          </Button>
        </div>
      </div>
    </div>
  );
}

function BlindVideo({
  label,
  refCallback,
  videoId,
  url,
}: {
  label: string;
  refCallback: (node: HTMLVideoElement | null) => void;
  videoId: string;
  url: string;
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex size-8 items-center justify-center rounded-md border bg-muted text-sm font-semibold">
          {label}
        </div>
      </div>
      <div className="overflow-hidden rounded-md border bg-black">
        <video
          key={`${videoId}:${url}`}
          ref={refCallback}
          src={url}
          loop
          playsInline
          preload="auto"
          className="aspect-video w-full bg-black object-contain"
        />
      </div>
    </div>
  );
}
